import type { Object3D } from 'three';
import {
  AssemblyReferenceCopyLineageRole,
  type AssemblyReferenceCopyLineageMetadata,
} from './AssemblyReferenceCopyTypes';

/** Object3D.metadata 中保存装配体引用复制族元数据的键。 */
const ASSEMBLY_REFERENCE_COPY_LINEAGE_METADATA_KEY = 'assemblyReferenceCopyLineage';

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 读取非空字符串。
 *
 * @param value 待读取值。
 * @returns 非空字符串；无效时返回空字符串。
 */
function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 读取引用复制族角色。
 *
 * @param value 待读取值。
 * @returns 有效角色；无效时返回 null。
 */
function readLineageRole(value: unknown): AssemblyReferenceCopyLineageRole | null {
  return value === AssemblyReferenceCopyLineageRole.OriginOccurrence
    || value === AssemblyReferenceCopyLineageRole.ReferenceOccurrence
      ? value
      : null;
}

/**
 * 克隆引用复制族元数据，避免外部持有可变引用。
 *
 * @param metadata 源元数据。
 * @returns 克隆后的元数据。
 */
function cloneLineageMetadata(
  metadata: AssemblyReferenceCopyLineageMetadata,
): AssemblyReferenceCopyLineageMetadata {
  return {
    referenceFamilyId: metadata.referenceFamilyId,
    definitionId: metadata.definitionId,
    originOccurrenceId: metadata.originOccurrenceId,
    role: metadata.role,
    ...(metadata.copiedFromOccurrenceId ? { copiedFromOccurrenceId: metadata.copiedFromOccurrenceId } : {}),
  };
}

/**
 * 从普通记录中解析引用复制族元数据。
 *
 * @param value 待解析值。
 * @returns 有效元数据；无效时返回 null。
 */
function parseLineageMetadata(value: unknown): AssemblyReferenceCopyLineageMetadata | null {
  if (!isRecord(value)) {
    return null;
  }

  const referenceFamilyId = readString(value.referenceFamilyId);
  const definitionId = readString(value.definitionId);
  const originOccurrenceId = readString(value.originOccurrenceId);
  const role = readLineageRole(value.role);
  if (!referenceFamilyId || !definitionId || !originOccurrenceId || !role) {
    return null;
  }

  const copiedFromOccurrenceId = readString(value.copiedFromOccurrenceId);
  return {
    referenceFamilyId,
    definitionId,
    originOccurrenceId,
    role,
    ...(copiedFromOccurrenceId ? { copiedFromOccurrenceId } : {}),
  };
}

/**
 * 装配体引用复制族元数据服务。
 *
 * 设计说明：
 * 1. `referenceCopy` 只标记复制体，会影响导出是否跳过 display payload；
 * 2. `lineage` 同时写在原始 occurrence 与复制 occurrence 上，只描述“是否同源”；
 * 3. 因此删除原始 A 后，A_1 / A_2 仍能通过同一个 `referenceFamilyId` 判断来自同一定义。
 */
export class AssemblyReferenceCopyLineageMetadataService {
  /**
   * 写入引用复制族元数据。
   *
   * @param object 目标 Object3D。
   * @param metadata 引用复制族元数据。
   */
  public static writeMetadata(
    object: Object3D,
    metadata: AssemblyReferenceCopyLineageMetadata,
  ): void {
    object.metadata = {
      ...(isRecord(object.metadata) ? object.metadata : {}),
      [ASSEMBLY_REFERENCE_COPY_LINEAGE_METADATA_KEY]: cloneLineageMetadata(metadata),
    };
  }

  /**
   * 清理目标子树上的引用复制族元数据。
   *
   * @param root 目标 Object3D 子树根。
   */
  public static clearMetadataInTree(root: Object3D): void {
    root.traverse((object) => {
      if (!isRecord(object.metadata) || !(ASSEMBLY_REFERENCE_COPY_LINEAGE_METADATA_KEY in object.metadata)) {
        return;
      }

      const nextMetadata = { ...object.metadata };
      delete nextMetadata[ASSEMBLY_REFERENCE_COPY_LINEAGE_METADATA_KEY];
      object.metadata = nextMetadata;
    });
  }

  /**
   * 读取对象自身的引用复制族元数据。
   *
   * @param object 目标 Object3D。
   * @returns 引用复制族元数据；不存在时返回 null。
   */
  public static readOwnMetadata(object: Object3D | null | undefined): AssemblyReferenceCopyLineageMetadata | null {
    if (!object || !isRecord(object.metadata)) {
      return null;
    }

    return parseLineageMetadata(object.metadata[ASSEMBLY_REFERENCE_COPY_LINEAGE_METADATA_KEY]);
  }

  /**
   * 从对象自身或父链读取引用复制族元数据。
   *
   * @param object 起始 Object3D。
   * @returns 引用复制族元数据；不存在时返回 null。
   */
  public static readMetadataFromSelfOrAncestors(
    object: Object3D | null | undefined,
  ): AssemblyReferenceCopyLineageMetadata | null {
    let current = object ?? null;
    while (current) {
      const metadata = this.readOwnMetadata(current);
      if (metadata) {
        return metadata;
      }
      current = current.parent;
    }
    return null;
  }
}
